/**
 * ArchetypeQuiz — 7-question scorer at the end of Card 4. Coach
 * answers about the player on their roster, gets a primary +
 * secondary archetype suggestion. Hook into the diagnostic on Card 5
 * as the natural next step.
 *
 * Scoring model:
 *   Each answer carries weights across the four archetypes.
 *   Sum across all answered questions. Highest score = primary;
 *   second-highest = secondary. Confidence is the gap between the
 *   top two scores expressed as a percentage of the max possible.
 *
 * No persistence on purpose. The quiz is meant to be a quick read on
 * a specific player; coaches re-run it as they get more data. The
 * persistent record lives on Card 5 (PgDiagnostic).
 */

import React, { useState, useMemo } from "react";
import { ChevronRight, RotateCcw, ArrowRight, Compass, Zap, Crosshair, Map } from "lucide-react";
import { GOLD } from "../constants/theme";

const ARCHETYPE_META = {
  Quarterback:        { Icon: Map,       color: GOLD, blurb: "Perimeter-first creator. Reads the floor, delivers the high-value pass before the defense compensates." },
  Downhill:           { Icon: Zap,       color: GOLD, blurb: "Paint-pressure attacker. Two dribbles to the rim, then spray out to shooters and dump-off to the big." },
  ShotFirst:          { Icon: Crosshair, color: GOLD, blurb: "Gravity creator. The threat of their bucket bends the defense; passes happen because the threat is real." },
  FloorArchitect:     { Icon: Compass,   color: GOLD, blurb: "Geometry manipulator. Probes horizontally, draws defenders out of position, exploits the seam." },
};

const ARCHETYPE_LABEL = {
  Quarterback: "Quarterback",
  Downhill: "Downhill Creator",
  ShotFirst: "Shot-First",
  FloorArchitect: "Floor Architect",
};

const QUESTIONS = [
  {
    q: "When this player gets a ball screen, what do they look to do first?",
    options: [
      { label: "Pull up if open",                              w: { Quarterback: 0, Downhill: 0, ShotFirst: 3, FloorArchitect: 1 }},
      { label: "Drive past the defender to attack the rim",   w: { Quarterback: 0, Downhill: 3, ShotFirst: 1, FloorArchitect: 0 }},
      { label: "Snake the screen and read the help",          w: { Quarterback: 2, Downhill: 0, ShotFirst: 0, FloorArchitect: 3 }},
      { label: "Hit the roller or kick to the corner",        w: { Quarterback: 3, Downhill: 1, ShotFirst: 0, FloorArchitect: 1 }},
    ],
  },
  {
    q: "What stands out most on their game film?",
    options: [
      { label: "Pull-up middys and floaters in the lane",      w: { Quarterback: 0, Downhill: 1, ShotFirst: 0, FloorArchitect: 3 }},
      { label: "Pull-up 3s off the bounce",                    w: { Quarterback: 1, Downhill: 0, ShotFirst: 3, FloorArchitect: 0 }},
      { label: "Layups and finishes through contact",          w: { Quarterback: 0, Downhill: 3, ShotFirst: 0, FloorArchitect: 1 }},
      { label: "Skip passes that lead to teammate threes",     w: { Quarterback: 3, Downhill: 0, ShotFirst: 0, FloorArchitect: 1 }},
    ],
  },
  {
    q: "How do they handle a tight defender pressing them full-court?",
    options: [
      { label: "Methodical, uses change of pace to slip away", w: { Quarterback: 1, Downhill: 0, ShotFirst: 0, FloorArchitect: 3 }},
      { label: "Powers through with strength and burst",       w: { Quarterback: 0, Downhill: 3, ShotFirst: 1, FloorArchitect: 0 }},
      { label: "Crossover + step-back, then shoots over",      w: { Quarterback: 0, Downhill: 0, ShotFirst: 3, FloorArchitect: 1 }},
      { label: "Gets it to a teammate to reset, runs the play",w: { Quarterback: 3, Downhill: 0, ShotFirst: 0, FloorArchitect: 1 }},
    ],
  },
  {
    q: "Best descriptor of their tempo?",
    options: [
      { label: "One speed: fast",                              w: { Quarterback: 0, Downhill: 3, ShotFirst: 0, FloorArchitect: 0 }},
      { label: "Changes speeds constantly within a possession",w: { Quarterback: 1, Downhill: 0, ShotFirst: 1, FloorArchitect: 3 }},
      { label: "Quick decisions, gets the ball out fast",      w: { Quarterback: 3, Downhill: 1, ShotFirst: 0, FloorArchitect: 0 }},
      { label: "Patient, lets the play develop, then strikes", w: { Quarterback: 1, Downhill: 0, ShotFirst: 2, FloorArchitect: 2 }},
    ],
  },
  {
    q: "Crunch time, the team needs a bucket. What's the plan?",
    options: [
      { label: "Iso, let the PG create",                       w: { Quarterback: 0, Downhill: 1, ShotFirst: 3, FloorArchitect: 1 }},
      { label: "Ball screen, PG attacks the rim",              w: { Quarterback: 0, Downhill: 3, ShotFirst: 0, FloorArchitect: 1 }},
      { label: "Ball screen, PG reads the defense",            w: { Quarterback: 3, Downhill: 0, ShotFirst: 0, FloorArchitect: 2 }},
      { label: "Drag action, PG probes for the best look",     w: { Quarterback: 1, Downhill: 0, ShotFirst: 0, FloorArchitect: 3 }},
    ],
  },
  {
    q: "Where do they live on the floor most of the time?",
    options: [
      { label: "Outside the 3-point line, scanning",           w: { Quarterback: 3, Downhill: 0, ShotFirst: 1, FloorArchitect: 0 }},
      { label: "In the paint or attacking it",                 w: { Quarterback: 0, Downhill: 3, ShotFirst: 0, FloorArchitect: 1 }},
      { label: "On the wing, getting up shots",                w: { Quarterback: 0, Downhill: 0, ShotFirst: 3, FloorArchitect: 1 }},
      { label: "Mid-range, probing and repositioning",         w: { Quarterback: 1, Downhill: 0, ShotFirst: 0, FloorArchitect: 3 }},
    ],
  },
  {
    q: "What does their best pass look like?",
    options: [
      { label: "Cross-court skip to a corner shooter",         w: { Quarterback: 3, Downhill: 0, ShotFirst: 0, FloorArchitect: 1 }},
      { label: "Live-dribble dump-off to the big at the rim",  w: { Quarterback: 1, Downhill: 3, ShotFirst: 0, FloorArchitect: 0 }},
      { label: "Kickout off the bounce when help over-commits",w: { Quarterback: 0, Downhill: 1, ShotFirst: 3, FloorArchitect: 1 }},
      { label: "Wraparound bounce after meandering the lane",  w: { Quarterback: 1, Downhill: 0, ShotFirst: 0, FloorArchitect: 3 }},
    ],
  },
];

// localStorage key for the saved quiz state. Single key shared across
// the masterclass — coaches retake on the same player, so we don't
// need per-player history (PgDiagnostic covers that surface).
const QUIZ_STORAGE_KEY = "bam_mc_pg_archetype_quiz_v1";

function loadQuizState() {
  try {
    const raw = localStorage.getItem(QUIZ_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.answers)) return null;
    return parsed;
  } catch { return null; }
}
function saveQuizState(state) {
  try { localStorage.setItem(QUIZ_STORAGE_KEY, JSON.stringify(state)); } catch {}
}
function clearQuizState() {
  try { localStorage.removeItem(QUIZ_STORAGE_KEY); } catch {}
}

export function ArchetypeQuiz({ C, onOpenDiagnostic }) {
  // Hydrate from localStorage on mount so coaches who came back to the
  // chapter pick up where they left off (or land on their previous
  // result without re-answering).
  const [answers, setAnswers] = useState(() => {
    const saved = loadQuizState();
    if (saved && saved.answers.length === QUESTIONS.length) return saved.answers;
    return QUESTIONS.map(() => null);
  });
  const [idx, setIdx] = useState(() => {
    const saved = loadQuizState();
    if (saved && typeof saved.idx === "number") return Math.min(saved.idx, QUESTIONS.length - 1);
    return 0;
  });
  const [showResult, setShowResult] = useState(() => {
    const saved = loadQuizState();
    return !!(saved && saved.showResult);
  });

  // Persist whenever anything changes.
  React.useEffect(() => {
    saveQuizState({ answers, idx, showResult });
  }, [answers, idx, showResult]);

  const pickAnswer = (optIdx) => {
    const next = [...answers];
    next[idx] = optIdx;
    setAnswers(next);
    // Auto-advance: if not on the last question, jump to the next.
    if (idx < QUESTIONS.length - 1) {
      setTimeout(() => setIdx(i => i + 1), 200);
    } else {
      // Last question answered: reveal the result.
      setTimeout(() => setShowResult(true), 200);
    }
  };

  const reset = () => {
    setAnswers(QUESTIONS.map(() => null));
    setIdx(0);
    setShowResult(false);
    clearQuizState();
  };

  const scores = useMemo(() => {
    const totals = { Quarterback: 0, Downhill: 0, ShotFirst: 0, FloorArchitect: 0 };
    answers.forEach((aIdx, qIdx) => {
      if (aIdx == null) return;
      const w = QUESTIONS[qIdx].options[aIdx].w;
      Object.keys(totals).forEach(k => { totals[k] += w[k] || 0; });
    });
    return totals;
  }, [answers]);

  const ranked = useMemo(() => {
    return Object.entries(scores)
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => ({ key: k, score: v }));
  }, [scores]);

  const maxPossible = QUESTIONS.length * 3; // each question max 3 toward any one archetype

  if (showResult) {
    const primary = ranked[0];
    const secondary = ranked[1];
    const gap = primary.score - secondary.score;
 const confidenceLabel = gap >= 6 ? "High confidence" : gap >= 3 ? "Moderate confidence" : "Close call: secondary is in play";
    const Pri = ARCHETYPE_META[primary.key];
    const Sec = ARCHETYPE_META[secondary.key];
    return (
      <div style={shellStyle()}>
        <Header title="Your read" rightContent={<button onClick={reset} style={ghostBtn(C)}><RotateCcw size={12}/> Re-take</button>}/>

        {/* Primary card */}
        <div style={{padding:"22px 22px 10px"}}>
          <div style={{
            display:"flex",alignItems:"center",gap:14,padding:"16px 18px",
            borderRadius:12, border:`1px solid ${GOLD}66`,
            background:`linear-gradient(135deg, ${GOLD}1a 0%, ${GOLD}06 100%)`,
            marginBottom:14,
          }}>
            <div style={{flexShrink:0,width:48,height:48,borderRadius:10,background:`${GOLD}22`,border:`1px solid ${GOLD}66`,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Pri.Icon size={22} color={GOLD}/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:10,fontWeight:800,color:GOLD,letterSpacing:1.5,textTransform:"uppercase",marginBottom:2}}>Primary archetype</div>
              <div style={{fontSize:20,fontWeight:800,color:C.text,lineHeight:1.2}}>{ARCHETYPE_LABEL[primary.key]}</div>
              <div style={{fontSize:12,color:C.textMid,marginTop:6,lineHeight:1.5}}>{Pri.blurb}</div>
            </div>
          </div>

          {/* Secondary */}
          <div style={{
            display:"flex",alignItems:"center",gap:12,padding:"12px 16px",
            borderRadius:10, border:`1px solid ${C.cardBorder || C.border}`,
            background:"rgba(255,255,255,0.025)",
            marginBottom:14,
          }}>
            <div style={{flexShrink:0,width:36,height:36,borderRadius:8,background:`rgba(255,255,255,0.04)`,border:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Sec.Icon size={16} color={C.textMid}/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:9,fontWeight:800,color:C.textDim,letterSpacing:1.5,textTransform:"uppercase",marginBottom:2}}>Secondary</div>
              <div style={{fontSize:14,fontWeight:700,color:C.text}}>{ARCHETYPE_LABEL[secondary.key]}</div>
            </div>
          </div>

          {/* Full score bars */}
          <div style={{fontSize:10,fontWeight:800,color:C.textDim,letterSpacing:1.5,textTransform:"uppercase",marginBottom:8}}>Score breakdown</div>
          <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
            {ranked.map(({key, score}) => {
              const pct = Math.round((score / maxPossible) * 100);
              return (
                <div key={key} style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:130,fontSize:12,fontWeight:600,color: key === primary.key ? GOLD : C.textMid}}>{ARCHETYPE_LABEL[key]}</div>
                  <div style={{flex:1,height:6,background:"rgba(255,255,255,0.06)",borderRadius:3,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`, background: key === primary.key ? GOLD : `${GOLD}55`, transition:"width .35s ease"}}/>
                  </div>
                  <div style={{width:36,textAlign:"right",fontSize:11,fontFamily:"'JetBrains Mono', monospace",color:C.textDim}}>{score}</div>
                </div>
              );
            })}
          </div>

          <div style={{fontSize:12,color:C.textDim,lineHeight:1.5,marginBottom:14}}>
            <strong style={{color:C.text}}>{confidenceLabel}.</strong> {gap >= 3
              ? "Your player's strengths point clearly in one direction. The secondary is worth tracking as a hedge."
              : "Two archetypes share the lead. This usually means a young player who hasn't fully settled, or a hybrid creator. Run the diagnostic to dig in."}
          </div>
        </div>

        <div style={{display:"flex",gap:10,padding:"14px 20px",borderTop:`1px solid ${GOLD}22`,background:"rgba(0,0,0,0.12)"}}>
          <button onClick={reset} style={{...ghostBtn(C), flex:1, justifyContent:"center"}}>
            <RotateCcw size={12}/> Run again
          </button>
          {/* Real chapter navigation via the reader (goToChapter through
              renderBlock ctx) — this used to be a window.alert telling the
              coach to go find Chapter 5 themselves. */}
          {onOpenDiagnostic && (
            <button onClick={onOpenDiagnostic} style={{...goldBtn, flex:1, justifyContent:"center"}}>
              Run the full diagnostic <ArrowRight size={13}/>
            </button>
          )}
        </div>
      </div>
    );
  }

  // QUIZ VIEW
  const q = QUESTIONS[idx];
  return (
    <div style={shellStyle()}>
      <Header title={`Question ${idx + 1} of ${QUESTIONS.length}`}
        rightContent={
          <div style={{display:"flex",gap:4}}>
            {QUESTIONS.map((_, i) => (
              <div key={i} style={{
                width: i === idx ? 18 : 8, height: 6, borderRadius: 3,
                background: i === idx ? GOLD : answers[i] != null ? `${GOLD}66` : C.border,
                transition: "width .2s ease, background .2s ease",
              }}/>
            ))}
          </div>
        }
      />

      <div style={{padding:"22px 22px 8px"}}>
        <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:18,lineHeight:1.4}}>{q.q}</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {q.options.map((opt, i) => {
            const isSelected = answers[idx] === i;
            return (
              <button key={i} onClick={() => pickAnswer(i)}
                style={{
                  display:"flex",alignItems:"center",gap:12,
                  padding:"12px 16px", borderRadius:10,
                  textAlign:"left", cursor:"pointer",
                  background: isSelected ? `${GOLD}18` : "rgba(255,255,255,0.025)",
                  border: `1px solid ${isSelected ? GOLD : C.cardBorder || C.border}`,
                  color: C.text, fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13, fontWeight: 600,
                  transition: "background .15s ease, border-color .15s ease",
                }}>
                <div style={{
                  flexShrink:0, width:18, height:18, borderRadius:"50%",
                  border:`1.5px solid ${isSelected ? GOLD : C.border}`,
                  background: isSelected ? GOLD : "transparent",
                  display:"flex",alignItems:"center",justifyContent:"center",
                }}>
                  {isSelected && <div style={{width:8,height:8,borderRadius:"50%",background:"#111"}}/>}
                </div>
                <span style={{flex:1}}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{display:"flex",gap:10,padding:"14px 20px",borderTop:`1px solid ${GOLD}22`,background:"rgba(0,0,0,0.12)"}}>
        <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}
          style={{...ghostBtn(C), flex:1, justifyContent:"center", opacity: idx === 0 ? 0.5 : 1, cursor: idx === 0 ? "default" : "pointer"}}>
          Previous
        </button>
        <button onClick={() => {
          if (idx < QUESTIONS.length - 1) setIdx(i => i + 1);
          else setShowResult(true);
        }} disabled={answers[idx] == null}
          style={{...goldBtn, flex:1, justifyContent:"center",
            opacity: answers[idx] == null ? 0.5 : 1,
            cursor: answers[idx] == null ? "default" : "pointer"}}>
          {idx < QUESTIONS.length - 1 ? "Next" : "See result"} <ChevronRight size={13}/>
        </button>
      </div>
    </div>
  );
}

// ── Layout / styles ───────────────────────────────────────────────

function Header({ title, rightContent }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "12px 20px",
      borderBottom: `1px solid ${GOLD}22`,
      background: "linear-gradient(180deg, rgba(226,221,159,0.10) 0%, rgba(226,221,159,0.02) 100%)",
    }}>
      <div style={{
        fontSize: 11, fontWeight: 800, color: GOLD,
        letterSpacing: 1.5, textTransform: "uppercase",
      }}>{title}</div>
      {rightContent}
    </div>
  );
}

const shellStyle = () => ({
  margin: "24px 0 12px",
  borderRadius: 14,
  border: `1px solid ${GOLD}55`,
  background: `linear-gradient(180deg, rgba(226,221,159,0.05), rgba(226,221,159,0.01))`,
  overflow: "hidden",
});

const goldBtn = {
  padding: "8px 14px", borderRadius: 8, border: "none",
  background: GOLD, color: "#111",
  fontSize: 12, fontWeight: 800, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "'DM Sans', sans-serif",
};

const ghostBtn = (C) => ({
  padding: "7px 12px", borderRadius: 8,
  background: "transparent", border: `1px solid ${C.border}`,
  color: C.textMid, fontSize: 12, fontWeight: 700, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "'DM Sans', sans-serif",
});

export default ArchetypeQuiz;
